import { getSafeURLParameters } from '@hc-frontend/core-utils-dom';

/**
 * URL parameters that, on their own, mark a request as a marketing landing
 * rather than in-funnel navigation. Any one of these opens the presence gate.
 *
 * Validated against 1,000 stored production landing URLs: all 340
 * acquisition-bearing rows carried `SRC`, and no row carried a click id without
 * it — so this list has no known coverage gap. The click ids are included
 * anyway, defensively: a landing that somehow arrives with only `msclkid` or
 * `gbraid` should still be recognised.
 */
const GATE_PARAMETERS = [
  'src',
  'utm_source',
  'gclid',
  'msclkid',
  'gbraid',
] as const;

/**
 * Parameters compared to decide whether the acquisition actually *changed*.
 *
 * Wider than {@link GATE_PARAMETERS} on purpose. The campaign-level fields
 * (`SRC` / `utm_*`) alone distinguished only ~28% of production landings,
 * because a single campaign covers many clicks: one `SRC`+`utm_campaign` pair
 * was seen spanning 25 distinct `gclid` values, and one affiliate `SRC` spanned
 * 11 distinct `partner_sub_id` values. Comparing the click and affiliate ids by
 * **value** raises that to ~85%, which matters because those ids drive
 * ad-platform conversion import and affiliate payout attribution.
 *
 * Deliberately excluded:
 * - `fbclid` — the lead carries a dedicated `facebook_click_id` column that is
 *   populated independently of the landing URL, so it is already handled.
 * - `tblci` — 3 occurrences per day in production; not worth the key width.
 * - `utm_term` / `utm_content` / `creative_id` — keyword- and creative-level
 *   churn would make almost every re-entry look like a new landing.
 */
const COMPARISON_PARAMETERS = [
  'src',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'gclid',
  'msclkid',
  'gbraid',
  'ircid',
  'partner_sub_id',
] as const;

/**
 * Builds a normalized "acquisition key" from a landing URL's marketing
 * parameters — see {@link COMPARISON_PARAMETERS} for the fields and why.
 *
 * Returns `null` when the URL carries **no** acquisition signal at all — this
 * is the guard that keeps intra-funnel navigation (survey step → survey step,
 * which carries no fresh marketing params) from looking like a new landing.
 *
 * @param url - The landing URL to inspect.
 * @returns A lowercased, pipe-joined acquisition key, or `null` when the URL
 * has no marketing parameters.
 */
function acquisitionKey(url: string): string | null {
  const parameters = getSafeURLParameters(url, true);

  // No marketing signal on this request → not a fresh landing.
  if (!GATE_PARAMETERS.some((parameter) => parameters[parameter])) {
    return null;
  }

  return COMPARISON_PARAMETERS.map((parameter) => parameters[parameter] ?? '')
    .join('|')
    .toLowerCase();
}

/**
 * Whether a URL carries any acquisition (marketing) parameters. Used as a cheap
 * pre-check to skip work (and an extra prospect lookup) on ordinary in-funnel
 * navigation, which carries no fresh marketing signal.
 *
 * @param url - The URL to inspect.
 * @returns `true` when the URL has at least one acquisition parameter.
 */
export function hasAcquisitionParams(url: string): boolean {
  return acquisitionKey(url) !== null;
}

/**
 * Decides whether the current request represents a **new marketing landing**
 * relative to the landing URL stored on the prospect.
 *
 * The comparison is intentionally on acquisition parameters, not the raw URL or
 * pathname, so navigating between funnel steps (same/no acquisition params) is
 * never treated as a new landing while a genuine re-entry from a different
 * campaign is.
 *
 * @param stored - The landing URL currently stored on the prospect.
 * @param current - The landing URL of the current request.
 * @returns `true` when the current request carries acquisition params that
 * differ from the stored prospect's acquisition (including the case where the
 * stored prospect had none); `false` otherwise (leave the landing untouched).
 */
export function hasLandingChanged(stored: string, current: string): boolean {
  const currentKey = acquisitionKey(current);

  // Current request has no acquisition signal → nothing to refresh.
  if (currentKey === null) {
    return false;
  }

  return acquisitionKey(stored) !== currentKey;
}
